import {
  task,
  TaskCreateBodyParams,
  TaskCreateResponse,
  TaskPartialUpdateBodyParams,
  TaskPartialUpdateResponse,
} from '@fireflyhealth/api'
import { useMutation } from '~/shared/hooks/query'
import { QueryClient, useQueryClient } from '@tanstack/react-query'
import { getCaseKey, getInboxKey, listCasesKey, listPersonTodoKey } from '~/shared/lib/queryKeys'
import { setDueDateToEOD } from '~/shared/lib/date'

// Update payloads route on `id` (the PATCH path param) and otherwise carry snake_case body fields.
type TaskUpdatePayload = TaskPartialUpdateBodyParams & { id?: number }

type TaskResponse = TaskCreateResponse | TaskPartialUpdateResponse

const updateTask = async (id: number, payload: TaskUpdatePayload) => {
  // `id` is the PATCH path param, so keep it out of the request body.
  const { id: _id, ...rest } = payload
  const body: TaskPartialUpdateBodyParams = {
    ...rest,
    // Due date is converted to the selected date at 19:00 EST.
    due_date: rest.due_date ? setDueDateToEOD(rest.due_date) : undefined,
  }
  const { data } = await task.partialUpdate({ id }, { data: body })
  return data
}

const createTask = async (payload: TaskCreateBodyParams) => {
  const body: TaskCreateBodyParams = {
    ...payload,
    // Due date is converted to the selected date at 19:00 EST.
    due_date: payload.due_date ? setDueDateToEOD(payload.due_date) : undefined,
  }
  const { data } = await task.create({ data: body })
  return data
}

export const useDeleteTasks = () => {
  const client = useQueryClient()

  return useMutation(
    async (tasks: Array<{ id: number }>) => {
      await Promise.all(tasks.map(({ id }) => task.destroy({ id })))
    },
    {
      // Bulk delete only carries IDs (no patient/person), so invalidate each
      // task-dependent key as a prefix instead of per-task like the sibling hooks.
      onSuccess: async () => {
        await client.cancelQueries({ queryKey: [listCasesKey] })
        await client.invalidateQueries({ queryKey: [listCasesKey] })
        await client.cancelQueries({ queryKey: [listPersonTodoKey] })
        await client.invalidateQueries({ queryKey: [listPersonTodoKey] })
        await client.cancelQueries({ queryKey: [getInboxKey] })
        await client.invalidateQueries({ queryKey: [getInboxKey] })
        await client.cancelQueries({ queryKey: [getCaseKey] })
        await client.invalidateQueries({ queryKey: [getCaseKey] })
      },
    },
    { error: 'Failed to delete task' },
  )
}

export const useAddTask = () => {
  const client = useQueryClient()

  return useMutation(
    createTask,
    {
      onSuccess: async (payload: TaskCreateResponse) => {
        await invalidateTaskDependentQueries(client, payload)
      },
    },
    { error: 'Failed to add task' },
  )
}

export const useUpdateTask = () => {
  const client = useQueryClient()

  return useMutation(
    (payload: TaskUpdatePayload) => {
      // `id` is the PATCH path param. Without it there is no task to update, and returning
      // nothing would leave the mutation reporting success for a request never made — so fail
      // and let the caller's error handling see it.
      if (payload.id == null) {
        throw new Error('Cannot update a task without an id')
      }
      return updateTask(payload.id, payload)
    },
    {
      onSuccess: async (payload: TaskPartialUpdateResponse) => {
        await invalidateTaskDependentQueries(client, payload)
      },
    },
    { error: 'Failed to update task' },
  )
}

async function invalidateTaskDependentQueries(client: QueryClient, payload: TaskResponse) {
  await client.cancelQueries({ queryKey: [listCasesKey, payload.patient] })
  await client.invalidateQueries({ queryKey: [listCasesKey, payload.patient] })
  await client.cancelQueries({ queryKey: [listPersonTodoKey, payload.person] })
  await client.invalidateQueries({ queryKey: [listPersonTodoKey, payload.person] })
  await client.cancelQueries({ queryKey: [getInboxKey] })
  await client.invalidateQueries({ queryKey: [getInboxKey] })
  await client.cancelQueries({ queryKey: [getCaseKey] })
  await client.invalidateQueries({ queryKey: [getCaseKey] })
}
